// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { Card, CardHeader, CardBody, Button, FormControl } from '../components/ui';
import api from '../services/api';
import {
  CONTENT_BLOCKER_CLEAR_EVENT,
  CONTENT_BLOCKER_EVENT,
  isContentBlockerDetected
} from '../utils/contentBlockerSignals';
import { canAccessDocumentDigitizationPage } from '../config/documentFeatures';
import '../styles/home.css';

const MySwal = withReactContent(Swal);

const FEATURED_ACTION_ID = 'sales';

const SALE_STEPS = [
  { id: 'offer', label: 'Oferta', icon: 'bi-file-text' },
  { id: 'data', label: 'Datos personales', icon: 'bi-person' },
  { id: 'sms', label: 'Verificación SMS', icon: 'bi-chat-dots' },
  { id: 'bio', label: 'Biometría', icon: 'bi-fingerprint' },
  { id: 'sign', label: 'Firma', icon: 'bi-pen' }
];

function HomePage() {
  const navigate = useNavigate();
  useAuth();
  const [showDniModal, setShowDniModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [dni, setDni] = useState('');
  const [observationsCount, setObservationsCount] = useState(0);
  const [isContentBlockerVisible, setIsContentBlockerVisible] = useState(() => isContentBlockerDetected());

  const mainActions = [
    {
      id: 'sales',
      title: 'Hacer una Venta',
      description: 'Crear una nueva venta a crédito',
      icon: 'bi-cart-plus',
      color: 'primary',
      route: '/ventas',
      badge: null,
      requiresDni: true,
      action: 'sale'
    },
    {
      id: 'viewSales',
      title: 'Ver ventas',
      description: 'Listado e historial de ventas',
      icon: 'bi-cart',
      color: 'primary',
      route: '/ventas',
      badge: null,
      requiresDni: false,
      action: null
    },
    {
      id: 'collections',
      title: 'Cobrar una Cuota',
      description: 'Registrar pagos de cuotas',
      icon: 'bi-cash-coin',
      color: 'success',
      route: '/cobros',
      badge: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? null : 'Próximamente',
      requiresDni: true,
      action: 'collection'
    },
    {
      id: 'documents',
      title: 'Digitalizar Documentos',
      description: 'Subir documentos de ventas',
      icon: 'bi-upload',
      color: 'info',
      route: '/documentos',
      badge: canAccessDocumentDigitizationPage() ? null : 'Próximamente',
      requiresDni: false,
      action: null
    },
    {
      id: 'zreports',
      title: 'Cierre Z',
      description: 'Generar reportes de cierre de caja',
      icon: 'bi-receipt',
      color: 'warning',
      route: '/reportes-z',
      badge: process.env.REACT_APP_ENABLE_ZREPORT_MANAGEMENT === 'true' ? null : 'Próximamente',
      requiresDni: false,
      action: null
    },
    {
      id: 'observations',
      title: 'Observaciones',
      description: 'Gestionar observaciones de ventas',
      icon: 'bi-exclamation-circle',
      color: 'info',
      route: '/observaciones',
      badge: process.env.REACT_APP_ENABLE_OBSERVATIONS_MANAGEMENT === 'true' ? null : 'Próximamente',
      requiresDni: false,
      action: null
    }
  ];

  const quickActions = mainActions.filter(
    (a) => a.id !== FEATURED_ACTION_ID && a.badge !== 'Próximamente'
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showDniModal) closeDniModal();
    };
    if (showDniModal) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDniModal]);

  useEffect(() => {
    const fetchObservationsCount = async () => {
      if (process.env.REACT_APP_ENABLE_OBSERVATIONS_MANAGEMENT !== 'true') {
        setObservationsCount(0);
        return;
      }

      try {
        const response = await api.get('/observations/count');
        setObservationsCount(response?.data?.count ?? 0);
      } catch (error) {
        console.error('Error al obtener conteo de observaciones:', error);
        setObservationsCount(0);
      }
    };

    fetchObservationsCount();
  }, []);

  useEffect(() => {
    const onSignal = () => setIsContentBlockerVisible(true);
    const onClear = () => setIsContentBlockerVisible(false);
    setIsContentBlockerVisible(isContentBlockerDetected());
    window.addEventListener(CONTENT_BLOCKER_EVENT, onSignal);
    window.addEventListener(CONTENT_BLOCKER_CLEAR_EVENT, onClear);
    return () => {
      window.removeEventListener(CONTENT_BLOCKER_EVENT, onSignal);
      window.removeEventListener(CONTENT_BLOCKER_CLEAR_EVENT, onClear);
    };
  }, []);

  const warnContentBlocker = () => {
    MySwal.fire({
      icon: 'warning',
      title: 'Detectamos un bloqueador',
      text: 'Desactivalo para este sitio antes de iniciar la venta. En el icono del bloqueador, pausalo en este dominio y recargá.',
    });
  };

  const handleActionClick = (action) => {
    if (action.action === 'sale' && isContentBlockerVisible) {
      warnContentBlocker();
      return;
    }
    if (action.requiresDni) {
      setPendingAction(action);
      setShowDniModal(true);
    } else {
      navigate(action.route);
    }
  };

  const handleDniSubmit = () => {
    if (pendingAction?.action === 'sale' && isContentBlockerVisible) {
      warnContentBlocker();
      return;
    }
    if (!dni || dni.length < 7 || dni.length > 8) {
      MySwal.fire({
        icon: 'warning',
        title: 'DNI inválido',
        text: 'El DNI debe tener entre 7 y 8 caracteres.',
      });
      return;
    }
    navigate(`/clientes/${dni}`, { state: { pendingAction: pendingAction?.action || 'sale' } });
    setShowDniModal(false);
    setPendingAction(null);
    setDni('');
  };

  const handleDniChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && value.length <= 8)) setDni(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleDniSubmit();
  };

  const closeDniModal = () => {
    setShowDniModal(false);
    setPendingAction(null);
    setDni('');
  };

  const submitFeaturedDni = () => {
    if (isContentBlockerVisible) {
      warnContentBlocker();
      return;
    }
    if (!dni || dni.length < 7 || dni.length > 8) {
      MySwal.fire({
        icon: 'warning',
        title: 'DNI inválido',
        text: 'El DNI debe tener entre 7 y 8 caracteres.',
      });
      return;
    }
    navigate(`/clientes/${dni}`, { state: { pendingAction: 'sale' } });
    setDni('');
  };

  return (
    <div className="page-content home-page rg-home rg-shell-content">
      <div className="home-page__grid">
        {/* Sidebar: Acciones rápidas */}
        <aside className="home-page__sidebar">
          <Card className="home-page__sidebar-card">
            <CardHeader className="home-page__sidebar-header">
              <h2 className="home-page__sidebar-title">
                <i className="bi bi-lightning-charge-fill home-page__sidebar-title-icon" aria-hidden />
                <span>Acciones rápidas</span>
              </h2>
            </CardHeader>
            <CardBody className="home-page__sidebar-body">
              <ul className="home-page__quick-list" role="list">
                {quickActions.map((action) => {
                  const isObservationsAction = action.id === 'observations';
                  const hasObservations = isObservationsAction && observationsCount > 0;
                  const actionAriaLabel = hasObservations
                    ? `${action.title}, ${observationsCount} pendientes o en proceso`
                    : `${action.title}. ${action.description}`;

                  return (
                    <li key={action.id}>
                      <button
                        type="button"
                        className="home-page__quick-item rg-focusable"
                        onClick={() => handleActionClick(action)}
                        aria-label={actionAriaLabel}
                      >
                        <span className="home-page__quick-icon">
                          <i className={`bi ${action.icon}`} aria-hidden />
                        </span>
                        <span className="home-page__quick-text">
                          <span className="home-page__quick-label-wrap">
                            <span className="home-page__quick-label">{action.title}</span>
                            {hasObservations && (
                              <span className="home-page__quick-count-badge" aria-hidden>
                                {observationsCount}
                              </span>
                            )}
                          </span>
                          <span className="home-page__quick-desc">{action.description}</span>
                        </span>
                        <i className="bi bi-chevron-right home-page__quick-chevron" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </CardBody>
          </Card>
        </aside>

        {/* Main: Nueva venta */}
        <main className="home-page__main">
          <Card className="home-page__featured-card">
            <CardBody className="home-page__featured-body">
              <div className="home-page__featured-icon">
                <i className="bi bi-cart-plus" aria-hidden />
              </div>
              <h3 className="home-page__featured-title">Nueva venta</h3>
              <p className="home-page__featured-desc">
                Ingresa el DNI del cliente y presiona buscar para iniciar el crédito.
              </p>

              <div className="home-page__featured-form rg-spacing-stack">
                <div className="rg-dni-surface">
                  <div className="home-page__featured-row rg-dni-group">
                    <FormControl
                      id="home-dni"
                      placeholder="Ej: 30.123.456"
                      value={dni}
                      onChange={handleDniChange}
                      onKeyDown={(e) => e.key === 'Enter' && submitFeaturedDni()}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      iconLeft={<i className="bi bi-person-vcard" />}
                      className="home-page__featured-field"
                    />
                    <Button
                      variant="primary"
                      onClick={submitFeaturedDni}
                      disabled={!dni || dni.length < 7 || isContentBlockerVisible}
                      className="home-page__featured-btn"
                    >
                      <i className="bi bi-search" aria-hidden />
                      Buscar Cliente
                    </Button>
                  </div>
                </div>
              </div>

              <div className="home-page__stepper" role="navigation" aria-label="Pasos de la venta">
                {SALE_STEPS.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <span className="home-page__stepper-step">
                      <i className={`bi ${step.icon}`} aria-hidden />
                      <span>{step.label}</span>
                    </span>
                    {index < SALE_STEPS.length - 1 && (
                      <i className="bi bi-chevron-right home-page__stepper-sep" aria-hidden />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardBody>
          </Card>
        </main>
      </div>

      {/* Modal DNI para acciones que lo requieren (ej. Cobrar cuota) */}
      {showDniModal && (
        <>
          <div
            className="modal-backdrop fade show home-modal-backdrop"
            onClick={closeDniModal}
            aria-hidden
          />
          <div className="modal fade show home-modal" tabIndex={-1} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered home-modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-person-vcard me-2" aria-hidden />
                    Buscar Cliente
                  </h5>
                  <button type="button" className="btn-close" onClick={closeDniModal} aria-label="Cerrar" />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      DNI del Cliente <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-person-vcard" aria-hidden />
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Ingrese DNI del cliente..."
                        value={dni}
                        onChange={handleDniChange}
                        onKeyDown={handleKeyDown}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoFocus
                      />
                    </div>
                    <small className="text-muted">El DNI debe tener entre 7 y 8 dígitos</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <Button variant="secondary" onClick={closeDniModal}>Cancelar</Button>
                  <Button
                    variant="primary"
                    onClick={handleDniSubmit}
                    disabled={!dni || dni.length < 7}
                  >
                    <i className="bi bi-search me-2" aria-hidden />
                    Buscar Cliente
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HomePage;
